/** Orden anzeigen, sammeln und die Bilanz der Regierungszeit als Bild teilen. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useS } from '../../store/game';
import { Panel } from '../../components/ui';
import { PC } from '../../engine/rules';
import { track } from '../../lib/telemetry';
import { MEDAL, MEDALS, eraOf, medalsNow, type Era } from './medals';
import { earn, loadLocal, onMedals } from './store';
import { drawCard } from './card';

export function useEarned() {
  const [e, setE] = useState(loadLocal);
  useEffect(() => onMedals(() => setE(loadLocal())), []);
  return e;
}

/** Bilder, die nicht geladen werden konnten (dann bleibt das Symbol). */
const noImg = new Set<string>();

export function Badge({ id, off, small }: { id: string; off?: boolean; small?: boolean }) {
  const m = MEDAL[id]; const [, force] = useState(0);
  if (!m) return null;
  const src = `/img/orden/${id}.webp`, img = !noImg.has(src);
  return <span className={'medal' + (off ? ' off' : '') + (small ? ' sm' : '') + (img ? ' img' : '')} title={`${m.n}: ${m.d}`} aria-hidden="true">
    <b>{m.sym}</b>
    {img && <img src={src} alt="" loading="lazy" decoding="async" onError={() => { noImg.add(src); force((x) => x + 1); }} />}
  </span>;
}

/** Am Ende einer Legislatur oder Wahl: Orden prüfen und neue eintragen. Gibt die neuen zurück. */
export function useMedalCheck(): string[] {
  const S = useS(); const [fresh, setFresh] = useState<string[]>([]); const key = useRef('');
  useEffect(() => {
    if (!S || S.mp || !['review', 'end'].includes(S.phase)) { setFresh([]); key.current = ''; return; }
    const k = `${S.seed}:${S.phase}:${S.m}`; if (key.current === k) return; key.current = k;
    const now = medalsNow(S), n = earn(now, { party: S.name, diff: (S.cfg as any)?.diff ?? 'normal' });
    setFresh(n);
    n.forEach((id) => track('Medal.earned', { id, difficulty: (S.cfg as any)?.diff ?? 'normal' }));
  }, [S?.phase, S?.m, S?.seed]); // eslint-disable-line react-hooks/exhaustive-deps
  return fresh;
}

/** Orden dieser Legislatur bzw. Wahl, neue hervorgehoben. */
export function MedalsPanel() {
  const S = useS(); const earned = useEarned(); const fresh = useMedalCheck();
  const now = useMemo(() => medalsNow(S), [S.phase, S.m]); // eslint-disable-line react-hooks/exhaustive-deps
  if (S.mp) return null;
  const total = Object.keys(earned).filter((k) => MEDAL[k]).length;
  return (
    <Panel title="Orden" aside={`${total} von ${MEDALS.length} gesammelt`} className="medalbox">
      {now.length ? <div className="medals">{now.map((id) => <div key={id} className={'mrow2' + (fresh.includes(id) ? ' new' : '')}><Badge id={id} /><div><b>{MEDAL[id].n}{fresh.includes(id) && <span className="newtag">Neu</span>}</b><span className="sub">{MEDAL[id].d}</span></div></div>)}</div>
        : <span className="sub">Diesmal kein Orden. Ein Blick in die Sammlung zeigt, was möglich ist.</span>}
      <MedalCollection compact />
    </Panel>
  );
}

/** Alle Orden: gesammelte in Farbe, offene grau mit Beschreibung als Ziel. */
export function MedalCollection({ compact }: { compact?: boolean }) {
  const earned = useEarned();
  const grid = <div className="mgrid">{MEDALS.map((m) => { const e = earned[m.id]; return (
    <div key={m.id} className={'mcell' + (e ? '' : ' off')}><Badge id={m.id} off={!e} /><div><b>{m.n}</b><span className="sub">{m.d}</span>{e && <span className="sub mdate">{new Date(e.at).toLocaleDateString('de-DE')} · {e.party}</span>}</div></div>); })}</div>;
  return compact ? <details className="lawmore"><summary>Alle Orden ansehen</summary>{grid}</details> : grid;
}

/** Wie sieht die Geschichte diese Regierung? Mit Bilanzkarte zum Teilen. */
export function EraPanel() {
  const S = useS(); const earned = useEarned();
  const era = useMemo(() => eraOf(S), [S.phase, S.m]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!era || S.mp) return null;
  const ids = medalsNow(S).length ? medalsNow(S) : [];
  const all = Object.keys(earned).filter((k) => MEDAL[k]);
  return (
    <Panel title={era.over ? 'So sieht dich die Geschichte' : 'Bisherige Bilanz'} aside={era.years} className="erabox">
      <div className="era"><span className="label">{S.name}</span><h3 className="eratitle">{era.title}</h3><p>{era.text}</p></div>
      <ShareCard era={era} medals={ids.length ? ids : all.slice(-6)} />
    </Panel>
  );
}

function ShareCard({ era, medals }: { era: Era; medals: string[] }) {
  const S = useS(); const [url, setUrl] = useState<string | null>(null); const [blob, setBlob] = useState<Blob | null>(null); const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    drawCard({ party: S.name, color: PC('YOU'), era, medals, diff: (S.cfg as any)?.diff ?? 'normal' }).then((b) => { if (off || !b) return; setBlob(b); setUrl((u) => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(b); }); });
    return () => { off = true; };
  }, [era.title, era.text, medals.join(','), S.name]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!url || !blob) return null;
  const file = new File([blob], 'kabinett-bilanz.png', { type: 'image/png' });
  const canShare = typeof navigator !== 'undefined' && !!navigator.canShare && navigator.canShare({ files: [file] });
  const text = `${era.title}: ${S.name} ${era.years}, ${era.laws} Gesetze${era.share != null ? `, ${String(era.share.toFixed(1)).replace('.', ',')} %` : ''}. Wie lange hält deine Regierung? https://kabinett-spiel.de`;
  return (
    <div className="sharecard">
      <img src={url} alt={`Bilanzkarte: ${era.title}, ${S.name}, ${era.years}`} width={1200} height={630} />
      <div className="actions">
        {canShare && <button type="button" className="yes" onClick={async () => { try { await navigator.share({ files: [file], text }); track('Share.card', { via: 'share' }); } catch { /* abgebrochen */ } }}>Bilanz teilen</button>}
        <a className={'btn' + (canShare ? '' : ' yes')} href={url} download="kabinett-bilanz.png" onClick={() => track('Share.card', { via: 'download' })}>Bild speichern</a>
        <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(text); setMsg('Text kopiert.'); track('Share.card', { via: 'text' }); } catch { setMsg('Kopieren nicht möglich.'); } }}>Text kopieren</button>
        {msg && <span className="sub">{msg}</span>}
      </div>
    </div>
  );
}
